// components/RealtimeProvider.tsx
import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { usePlasmicInvalidate } from "@plasmicapp/react-web/lib/data-sources";
import { supabaseClient } from "../lib/supabase/client";
import type { RealtimePostgresChangesPayload, RealtimeClient } from "@supabase/supabase-js";
import { subscriptionManager } from "../lib/realtime/RealtimeManager";

type ConnectionStatus = "CONNECTING" | "OPEN" | "CLOSING" | "CLOSED" | "UNKNOWN";

interface RealtimeContextValue {
  connectionState: ConnectionStatus;
  subscribedTables: string[];
}

const RealtimeContext = createContext<RealtimeContextValue>({
  connectionState: "CLOSED",
  subscribedTables: [],
});

export const useRealtimeStatus = () => useContext(RealtimeContext);

const getConnectionState = (rtClient: RealtimeClient): ConnectionStatus => {
  // Corrected: connectionState is a method, not a property.
  const state = rtClient.connectionState();
  return (state?.toUpperCase() as ConnectionStatus) || "UNKNOWN";
};

// Define a type for the state change callbacks for clarity.
// The internal type uses Function[], so we match that to avoid assignment errors.
//type StateChangeCallbacks = {
 // open: Array<() => void>;
  //close: Array<() => void>;
  //error: Array<() => void>;
//};

export const RealtimeProvider = ({ children }: { children: React.ReactNode }) => {
  const plasmicInvalidate = usePlasmicInvalidate();
  const [status, setStatus] = useState<RealtimeContextValue>({
    connectionState: "CLOSED",
    subscribedTables: [],
  });

  useEffect(() => {
    console.log("[RealtimeProvider] Mounting provider…");
    const rtClient = supabaseClient.realtime;
    
    // Cast to `any` because stateChangeCallbacks is an internal/private property
    const rtClientInternal = rtClient as any;

    const updateStatus = (source: string) => {
      const newStatus: RealtimeContextValue = {
        connectionState: getConnectionState(rtClient),
        subscribedTables: subscriptionManager.getRequiredTables(),
      };
      setStatus((prev) => {
        if (
          prev.connectionState === newStatus.connectionState &&
          prev.subscribedTables.join(",") === newStatus.subscribedTables.join(",")
        ) {
          return prev;
        }
        console.log(`[RealtimeProvider] [${source}] Status changed`, newStatus);
        return newStatus;
      });
    };

    const handleInvalidate = () => {
      console.log("[RealtimeProvider] Received plasmic_refresh_all ? calling plasmicInvalidate");
      plasmicInvalidate(["plasmic_refresh_all"]);
      updateStatus("invalidate");
    };
    window.addEventListener("plasmic_refresh_all", handleInvalidate);

    const onConnectionChange = () => updateStatus("connectionChange");
    
    // Safely push to internal callback arrays
    rtClientInternal.stateChangeCallbacks.open.push(onConnectionChange);
    rtClientInternal.stateChangeCallbacks.close.push(onConnectionChange);
    rtClientInternal.stateChangeCallbacks.error.push(onConnectionChange);

    updateStatus("initial");

    return () => {
      window.removeEventListener("plasmic_refresh_all", handleInvalidate);

      // Safely filter out our listeners on unmount
      rtClientInternal.stateChangeCallbacks.open = rtClientInternal.stateChangeCallbacks.open.filter(
        (cb: any) => cb !== onConnectionChange
      );

      rtClientInternal.stateChangeCallbacks.close = rtClientInternal.stateChangeCallbacks.close.filter(
        (cb: any) => cb !== onConnectionChange
      );

      rtClientInternal.stateChangeCallbacks.error = rtClientInternal.stateChangeCallbacks.error.filter(
        (cb: any) => cb !== onConnectionChange
      );
    };
  }, [plasmicInvalidate]);


  return (
    <RealtimeContext.Provider value={status}>
      {children}
    </RealtimeContext.Provider>
  );
};

// Consumer hook
  // eslint-disable-next-line react-hooks/exhaustive-deps
export function useRealtimeSubscription(
  tables: string[],
  onTrigger?: (payload: RealtimePostgresChangesPayload<{ [key: string]: unknown }>) => void
) {
  const componentId = useRef(Math.random().toString(36).substring(2, 9)).current;
  
  // 1. Calculate a stable key based on the CONTENT of the array, not the reference.
  const stableTablesKey = JSON.stringify([...tables].sort());

  // 2. Use a ref for the callback so it doesn't trigger re-subscriptions when the function identity changes.
  const onTriggerRef = useRef(onTrigger);
  useEffect(() => {
    onTriggerRef.current = onTrigger;
  }, [onTrigger]);

  useEffect(() => {
    const uniqueTables = [...new Set(tables)].filter(Boolean);
    // Only log if we actually have tables to subscribe to
    if (uniqueTables.length > 0) {
      console.log("[useRealtimeSubscription] Mount", componentId, "tables=", uniqueTables);
      
      // Pass a wrapper that calls the current ref
      subscriptionManager.add(componentId, uniqueTables, (payload) => {
        if (onTriggerRef.current) onTriggerRef.current(payload);
      });
    }

    return () => {
      if (uniqueTables.length > 0) {
        console.log("[useRealtimeSubscription] Unmount", componentId);
        subscriptionManager.remove(componentId);
      }
    };
    
    // 3. CRITICAL FIX: Remove 'tables' and 'onTrigger' from dependencies.
    // We rely ONLY on 'stableTablesKey' to know if the subscription needs to change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentId, stableTablesKey]); 
}
